// biome-ignore-all lint/security/noDangerouslySetInnerHtml: the last-comment body renders staff-authored note text through plainTextDisplayHtml, which HTML-escapes the whole comment before adding <br> markup — user input is never interpreted as HTML (same guard ShelterRequestView uses).
// =============================================================================
// TopUpShelterHeader.jsx
// =============================================================================
// The 3-card header for the SHELTER-facing Stock Top-up read view (TopUpReadView),
// mirroring the standard request's ShelterRequestView top strip so a top-up
// "looks like" a normal request to the DFV Service:
//
//   General Information | Last Comment (shelter-only) | Summary
//
// ⚠️ CROSS-REFERENCE: the General Information + Last Comment cards are a DELIBERATE
// DUPLICATE of the top 3-column strip in src/components/request/ShelterRequestView.jsx.
// (ShelterRequestView is monolithic + children-based, so it can't be reused for a
// childless top-up — this is the same "duplicate + cross-reference, don't refactor
// live code" call as the 5 Sep TopUpTopInfoStrip delivery block.) If you change the
// General-Info fields or the "From Zephyr" last-comment logic in EITHER file, check
// the OTHER.
//
// The Summary card differs ON PURPOSE: a top-up lists the SECTIONS the DFV Service
// ordered (no per-child dollar totals — a top-up has none), and its cost appears only
// once the picker has ordered it from the supplier.
//
// Props
// ─────
//   req                — the request object (camelCase dbToReq shape, as RequestDetail
//                        passes to ShelterRequestView)
//   sectionLabels      — string[] of the ordered section names, in section order
//   isShelterUser      — true for a DFV Service caseworker; false for a Zephyr viewer
//                        (who reads this read-only and uses the title-bar Comments
//                        button instead — the fence). Gates the Last Comment card.
//   notes              — saved comment array (for the "From Zephyr" latest note)
//   setShowCommentPanel — opens the comment slide-in
//   hasUnread          — db-77 unread-staff-comment flag (reddy-orange button)
//   onCommentsOpened   — clears the unread flag when the shelter opens Comments
// =============================================================================

import { plainTextDisplayHtml } from "../../services/richText";
import { C } from "../../utils/constants";
import { GLYPH } from "../../utils/glyphs";
import { formatPhone } from "../../utils/sanitize";

const BORDER = "#CFE0DE";

const labelCell = {
  fontSize: 11,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  width: 120,
  flexShrink: 0,
};

const cardShell = {
  display: "flex",
  flexDirection: "column",
  border: `1px solid ${BORDER}`,
  borderRadius: 10,
  overflow: "hidden",
};

const cardHeader = {
  background: C.blue,
  color: "white",
  padding: "11px 18px",
  fontWeight: 700,
  fontSize: 14,
  flexShrink: 0,
};

// One label / value row (hidden when the value is empty).
function Row({ label, value }) {
  if (!value) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        marginBottom: 6,
        paddingBottom: 6,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <div style={labelCell}>{label}</div>
      <div style={{ fontSize: 13, color: "#1A2E2B", lineHeight: 1.5, flex: 1 }}>{value}</div>
    </div>
  );
}

export function TopUpShelterHeader({
  req,
  sectionLabels,
  isShelterUser = true,
  notes = [],
  setShowCommentPanel,
  hasUnread = false,
  onCommentsOpened,
}) {
  const nums =
    req?.mobile || req?.phone ? (
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 14 }}>
        {req?.mobile ? <span>{formatPhone(req.mobile)}</span> : null}
        {req?.phone ? <span>{formatPhone(req.phone)}</span> : null}
      </div>
    ) : null;

  // 3 cards for a shelter (General | Last Comment | Summary); 2 for a Zephyr viewer
  // (General | Summary — they take comments through the title bar, so no card 2).
  const gridCols = isShelterUser ? "1fr 1fr 1fr" : "1fr 1fr";

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: gridCols,
        gap: 12,
        marginBottom: 14,
        alignItems: "stretch",
      }}
    >
      {/* General Information */}
      <div style={cardShell}>
        <div style={cardHeader}>General Information</div>
        <div style={{ padding: "14px 16px", flex: 1, display: "flex", flexDirection: "column" }}>
          <Row label="DFV Service Ref" value={req?.shelterRef} />
          <Row label="Contact Name" value={req?.contactName} />
          <Row label="Contact Email" value={req?.shelterEmail} />
          <Row label="Mobile / Phone" value={nums} />
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <div style={labelCell}>Delivery</div>
            <div style={{ fontSize: 13, color: "#1A2E2B", lineHeight: 1.5, flex: 1 }}>
              {req?.deliveryAddress || "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Last Comment — SHELTER-SIDE ONLY (the fence). A Zephyr viewer uses the
          internal Comments button in the title bar. Mirrors ShelterRequestView. */}
      {isShelterUser && (
        <div style={cardShell}>
          <div
            style={{
              background: C.blue,
              color: "white",
              padding: "6px 12px 6px 18px",
              fontWeight: 700,
              fontSize: 14,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span>Last Comment</span>
            <button
              type="button"
              onClick={function () {
                if (onCommentsOpened) onCommentsOpened();
                if (setShowCommentPanel) setShowCommentPanel(true);
              }}
              style={{
                background: hasUnread ? "#D84315" : C.blueLight,
                color: hasUnread ? "white" : C.blue,
                border: hasUnread ? "1px solid #FFFFFF" : `1px solid ${C.blue}`,
                borderRadius: 6,
                padding: "5px 12px",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              {`${GLYPH.speech} Add/View Comments`}
              {hasUnread ? (
                <span
                  style={{
                    background: "#FFFFFF",
                    color: "#D84315",
                    borderRadius: 10,
                    padding: "1px 8px",
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  New
                </span>
              ) : notes.length > 0 ? (
                <span
                  style={{
                    background: "rgba(255,255,255,0.25)",
                    borderRadius: 10,
                    padding: "1px 7px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {notes.length}
                </span>
              ) : null}
            </button>
          </div>
          <div style={{ padding: "14px 16px", flex: 1 }}>
            {(function () {
              // "From Zephyr" = staff-authored notes + approval summaries. Oldest-first, so
              // the LAST match is newest. (Internal staff notes live in
              // request_internal_notes, never in this array.)
              const zephyrNotes = notes.filter(function (n) {
                return n.byRole === "staff" || n._summary;
              });
              if (zephyrNotes.length === 0)
                return (
                  <div style={{ fontSize: 13, color: "#607D79", fontStyle: "italic" }}>
                    No updates yet. You will be notified when Zephyr responds.
                  </div>
                );
              const latest = zephyrNotes[zephyrNotes.length - 1];
              const maxLen = 300;
              const truncated = latest.text && latest.text.length > maxLen;
              return (
                <div>
                  <div style={{ fontSize: 11, color: "#607D79", marginBottom: 6 }}>
                    {latest.author} — {latest.date}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "#1A2E2B",
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: plainTextDisplayHtml(
                        truncated ? `${latest.text.slice(0, maxLen)}...` : latest.text
                      ),
                    }}
                  />
                  {truncated && (
                    <button
                      type="button"
                      onClick={function () {
                        if (onCommentsOpened) onCommentsOpened();
                        if (setShowCommentPanel) setShowCommentPanel(true);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#00897B",
                        fontSize: 16,
                        fontWeight: 600,
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        padding: "4px 0",
                        marginTop: 4,
                      }}
                    >
                      View full comment {GLYPH.arrowRight}
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Summary — the SECTIONS the DFV Service ordered (no counts). The cost line is a
          placeholder until the picker orders the stock from the supplier. */}
      <div style={cardShell}>
        <div style={cardHeader}>Summary</div>
        <div
          style={{
            padding: "14px 16px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ flex: 1 }}>
            {(sectionLabels || []).length === 0 ? (
              <div style={{ fontSize: 13, color: "#607D79", fontStyle: "italic" }}>
                No items on this top-up.
              </div>
            ) : (
              (sectionLabels || []).map(function (label) {
                return (
                  <div
                    key={label}
                    style={{
                      fontSize: 14,
                      padding: "6px 0",
                      borderBottom: `1px solid ${BORDER}`,
                      color: "#1A2E2B",
                    }}
                  >
                    {label}
                  </div>
                );
              })
            )}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#607D79",
              fontStyle: "italic",
              paddingTop: 10,
              marginTop: 4,
            }}
          >
            Cost of Top-up shown when ordered from Supplier.
          </div>
        </div>
      </div>
    </div>
  );
}
